import {
  GraphQLSchema,
  isListType,
  GraphQLWrappingType,
  isWrappingType,
  getNamedType,
  GraphQLOutputType,
  GraphQLNamedType,
  TypeDefinitionNode,
  FieldDefinitionNode,
  DocumentNode,
  validate as validateAst,
  GraphQLError,
  GraphQLObjectType,
  DirectiveNode,
  isTypeSubTypeOf,
  buildSchema,
  Kind,
} from 'query-api-graphql';
import {useMemo, useEffect} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {measureFn, start, stop} from 'performance/Perf';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  AsyncState,
  AsyncStatus,
  makeAsyncCall,
  NOT_FETCHED_ASYNC_STATE,
  SuccessAsyncState,
} from 'ui-library/hooks/useAsyncState';
import useOnMount from 'ui-library/hooks/useOnMount';
import APIUtil from 'util/APIUtil';
import sortedBy from 'util/sortedBy';

import {GraphQLField} from './graphql-field-selector/GraphQLFieldSelectorTypes';
import {
  fetchQueryApiSDL as fetchQueryApiSDLDAO,
  fetchQueryApiBacklinks as fetchQueryApiBacklinksDAO,
} from './QueryApiDAO';

/**
 * Get a list of types wrapping the named type of a `GraphQLOutputType`.
 *
 * Types are ordered from the outermost to the innermost type. For example, the list of the wrapping types of
 * `[X!]!` will be `[type corresponding to [X!]!, type corresponding to [X!], type corresponding to X!]`
 *
 * @example
 * const type = ... // type corresponding to [X!]!
 * getWrappingTypes(type) -> [GraphQLNonNullType<...>, GraphQLListType<...>, GraphQLNonNullType<...>]
 *
 * @package
 * */
export function getWrappingTypes(type: GraphQLOutputType): Array<GraphQLWrappingType> {
  const types = [];
  while (isWrappingType(type)) {
    types.push(type);
    type = type.ofType;
  }
  return types;
}

export interface QueryApiIntrospection {
  graphQLSchema: GraphQLSchema;

  /**
   * Return the query-type field corresponding to `fieldName`, or `null` if that field does not exist.
   */
  getQueryField: (fieldName: string) => GraphQLField | null;

  /**
   * Find the query-type field corresponding to `fieldName` and return that field's type's corresponding
   * schema id, if there is one.
   */
  getQueryFieldSchemaId: (fieldName: string) => string | null;

  /**
   * Return the query-type field that has type List(objects corresponding to `schemaId`), if there is one.
   *
   * In this query:
   * ```
   * query {
   *   benchling__PlasmidItems {
   *     name
   *   }
   * }
   * ```
   * benchling__PlasmidItems is the listing field for the Plasmid schema. So if the Plasmid schema has id
   * ts_123, then getListingFieldForSchemaId('ts_123') will return the `benchling__PlasmidItems` field.
   */
  getQueryListingFieldForSchema: (schemaId: string) => GraphQLField | null;

  /**
   * Determine if the input Benchling schema id has a corresponding List(objects corresponding to `scehmaId`)
   * field on the query-type.
   *
   * In this query:
   * ```
   * query {
   *   benchling__PlasmidItems {
   *     name
   *   }
   * }
   * ```
   * benchling__PlasmidItems is the listing field for the Plasmid schema. So if the Plasmid schema has id
   * ts_123, then hasListingFieldForSchemaId('ts_123') will return `true`.
   */
  hasQueryListingFieldForSchema: (schemaId: string) => boolean;

  /**
   * Determine if the input field is deprecated.
   */
  isDeprecatedField: (field: GraphQLField) => boolean;

  /**
   * Determine if the input field is a backlink field (for example, the field is a backlink to a result
   * schema).
   *
   * For example, if this is the SDL:
   * ```
   * type Result {
   *   entity: Plasmid
   * }
   *
   * type Plasmid {
   *   id: ID
   *   _resultBacklink(joinKey: String): [Result!]
   * }
   * ```
   * Then `isBacklinkField(_resultBacklinkField)` will return `true`.
   */
  isBacklinkField: (field: GraphQLField) => boolean;

  /**
   * Determine if the input field is derived from a computed schema field.
   * @param field
   */
  isUserComputedField: (field: GraphQLField) => boolean;

  /**
   * Determine if the input field is user defined (for example, the field corresponds to a tag schema field).
   */
  isUserDefinedField: (field: GraphQLField) => boolean;

  /**
   * Get the display value of a field.
   *
   * Where possible, this is based on schema field display names, but otherwise it falls back to
   * `field.name`.
   */
  getFieldDisplayName: (field: GraphQLField) => string;

  /**
   * Get the display name of a type.
   */
  getTypeDisplayName: (type: GraphQLNamedType) => string;

  /**
   * Validate a query against the GraphQL schema. Return an array of errors.
   *
   * The array will be empty if the query is valid.
   */
  getQueryAstValidationErrors: (queryAst: DocumentNode) => ReadonlyArray<GraphQLError>;

  /**
   * Given a backlink field, return the schema id it joins to.
   *
   * For example, if this is the SDL:
   * ```
   * type Result {     // Corresponds to Assay Schema with ID "assaysch_123"
   *   entity: Plasmid
   * }
   *
   * type Plasmid {
   *   id: ID
   *   _resultBacklink(joinKey: String): [Result!]
   * }
   * ```
   * Then `backlinkFieldToTargetSchemaId(_resultBacklinkField)` will return `"assaysch_123"`.
   */
  backlinkFieldToTargetSchemaId: (backlinkField: GraphQLField) => string;

  /**
   * Given an object type, finds the field on that type that is a backlink to the input schema id.
   *
   * For example, if this is the SDL:
   * ```
   * type Result {     // Corresponds to Assay Schema with ID "assaysch_123"
   *   entity: Plasmid
   * }
   *
   * type Plasmid {
   *   id: ID
   *   _resultBacklink(joinKey: String): [Result!]
   * }
   * ```
   * Then `targetSchemaIdToBacklinkField(PlasmidType, "assaysch_123")` will return `_resultBacklinkField`.
   */
  targetSchemaIdToBacklinkField: (type: GraphQLObjectType, schemaId: string) => GraphQLField | null;

  /**
   * Return whether or not the input object type has at least one backlink field to a result schema.
   */
  typeHasResultSchemaBacklinkFields: (type: GraphQLObjectType) => boolean;

  /**
   * Return the array of fields on the `targetType` that can be used to join to the `sourceType`.
   *
   * Any field whose named type is a supertype of `sourceType` is returned.
   *
   * The fields are ordered by specificity (generic "Any Entity" after specific schematized link fields),
   * then alphabetically.
   */
  getPossibleJoinKeyFields: (
    sourceType: GraphQLObjectType,
    targetType: GraphQLObjectType
  ) => Array<GraphQLField>;

  /**
   * Return all listable schemas, independent of the underlying Schema class.
   */
  getAllListableSchemas: () => Array<{id: string; name: string}>;
}

export type UseQueryApiIntrospectionOutput =
  | {status: QueryResultType.PENDING}
  | {status: QueryResultType.ERROR}
  | ({status: QueryResultType.SUCCESS} & QueryApiIntrospection);

function getDirective(
  astNode: TypeDefinitionNode | FieldDefinitionNode,
  directiveName: string
): DirectiveNode | null {
  return astNode.directives?.find((directive) => directive.name.value === directiveName) ?? null;
}

function getDirectiveStringArg(
  astNode: TypeDefinitionNode | FieldDefinitionNode,
  directiveName: string,
  argumentName: string
): string | null {
  const argument = getDirective(astNode, directiveName)?.arguments?.find(
    (arg) => arg.name.value === argumentName
  );
  return argument?.value.kind === Kind.STRING ? argument.value.value : null;
}

function fieldOrTypeToId(fieldOrType: GraphQLNamedType | GraphQLField): string | null {
  const astNode = fieldOrType?.astNode;
  if (astNode == null) {
    return null;
  }
  return getDirectiveStringArg(astNode, 'alias', 'id');
}

function fieldOrTypeToDisplayName(fieldOrType: GraphQLNamedType | GraphQLField): string | null {
  const astNode = fieldOrType?.astNode;
  if (astNode == null) {
    return null;
  }
  return getDirectiveStringArg(astNode, 'alias', 'displayName');
}

function buildSchemaIdToQueryListingFieldsMap(
  queryFields: Array<GraphQLField>
): Map<string, Array<GraphQLField>> {
  const schemaIdToListingFields = new Map<string, Array<GraphQLField>>();
  for (const queryField of queryFields) {
    if (!getWrappingTypes(queryField.type).some((type) => isListType(type))) {
      continue;
    }

    const namedType = getNamedType(queryField.type);
    const schemaId = fieldOrTypeToId(namedType);
    if (schemaId == null) {
      continue;
    }

    if (!schemaIdToListingFields.has(schemaId)) {
      schemaIdToListingFields.set(schemaId, []);
    }

    schemaIdToListingFields.get(schemaId)?.push(queryField);
  }
  return schemaIdToListingFields;
}

export function buildQueryApiIntrospection(sdl: string): QueryApiIntrospection {
  const graphQLSchema = buildSchema(sdl);
  const queryType = graphQLSchema.getQueryType();
  assert(queryType != null, 'Got null GraphQL query type');

  const queryFields = Object.values(queryType.getFields());

  const schemaIdToQueryListingFields = buildSchemaIdToQueryListingFieldsMap(queryFields);

  function getQueryListingFieldForSchema(schemaId: string): GraphQLField | null {
    const listingFields = schemaIdToQueryListingFields.get(schemaId) ?? [];
    assertAndContinue(listingFields.length <= 1, 'Found multiple query listing fields for schema', {
      schemaId,
      listingFields,
    });
    return listingFields.length === 0 ? null : listingFields[0];
  }

  function getFieldDisplayName(field: GraphQLField): string {
    return fieldOrTypeToDisplayName(field) ?? field.name;
  }

  function getTypeDisplayName(type: GraphQLNamedType): string {
    return fieldOrTypeToDisplayName(type) ?? type.name;
  }

  function isBacklinkField(field: GraphQLField): boolean {
    return field.astNode != null && getDirective(field.astNode, 'backlink') != null;
  }

  function isUserComputedField(field: GraphQLField): boolean {
    const isUserDefined = isUserDefinedField(field);
    const isComputed = field.astNode != null && getDirective(field.astNode, 'computed') != null;
    return isUserDefined && isComputed;
  }

  function isUserDefinedField(field: GraphQLField): boolean {
    return fieldOrTypeToId(field) != null;
  }

  function getQueryField(fieldName: string): GraphQLField | null {
    return queryType!.getFields()[fieldName] ?? null;
  }

  function getQueryFieldSchemaId(fieldName: string): string | null {
    const queryField = getQueryField(fieldName);

    return queryField == null ? null : (fieldOrTypeToId(getNamedType(queryField.type)) ?? null);
  }

  function backlinkFieldToTargetSchemaId(backlinkField: GraphQLField): string {
    assert(isBacklinkField(backlinkField), 'backlinkFieldToTargetSchemaId called with non-backlink field');
    const schemaId = fieldOrTypeToId(getNamedType(backlinkField.type));
    assert(schemaId != null, 'Got a backlink field whose type has no corresponding schema', {
      fieldName: backlinkField.name,
    });
    return schemaId;
  }

  function targetSchemaIdToBacklinkField(type: GraphQLObjectType, schemaId: string): GraphQLField | null {
    const field = Object.values(type.getFields()).find(
      (field) => isBacklinkField(field) && backlinkFieldToTargetSchemaId(field) === schemaId
    );
    return field ?? null;
  }

  function typeHasResultSchemaBacklinkFields(type: GraphQLObjectType): boolean {
    return Object.values(type.getFields()).some((field) => {
      if (!isBacklinkField(field)) {
        return false;
      }
      const fieldType = getNamedType(field.type);
      const schemaId = fieldOrTypeToId(fieldType);
      if (schemaId == null) {
        return false;
      }
      return APIUtil.isAssaySchema(schemaId);
    });
  }

  function getPossibleJoinKeyFields(
    sourceType: GraphQLObjectType,
    targetType: GraphQLObjectType
  ): Array<GraphQLField> {
    return sortedBy(
      Object.values(targetType.getFields()).filter((field) =>
        isTypeSubTypeOf(graphQLSchema, sourceType, getNamedType(field.type))
      ),
      [
        {accessor: (field) => (getNamedType(field.type) === sourceType ? -1 : 1)},
        {accessor: getFieldDisplayName},
      ]
    );
  }

  function getAllListableSchemas(): Array<{id: string; name: string}> {
    return Object.values(graphQLSchema.getTypeMap())
      .filter((type) => fieldOrTypeToId(type) != null)
      .map((type) => ({
        id: fieldOrTypeToId(type)!,
        name: getTypeDisplayName(type),
      }));
  }

  return {
    graphQLSchema,
    getQueryField,
    getQueryFieldSchemaId,
    getQueryListingFieldForSchema,
    hasQueryListingFieldForSchema: (schemaId: string) => getQueryListingFieldForSchema(schemaId) != null,
    getFieldDisplayName,
    isDeprecatedField: (field) => field.astNode != null && getDirective(field.astNode, 'deprecated') != null,
    isBacklinkField,
    isUserComputedField,
    isUserDefinedField,
    getTypeDisplayName,
    getQueryAstValidationErrors: (queryAst) => validateAst(graphQLSchema, queryAst),
    targetSchemaIdToBacklinkField,
    backlinkFieldToTargetSchemaId,
    typeHasResultSchemaBacklinkFields,
    getPossibleJoinKeyFields,
    getAllListableSchemas,
  };
}

// The query-api introspection query is cached in global state.
//
// The state key includes the schema names filter, so that the query is re-run when the schema names
// filter changes. schemaNamesFilter is optional, so the type of the parameter can be `Array<string> | undefined`.
const queryApiIntrospectionQueryGlobalState =
  makeUseGlobalClientState<AsyncState<string, Array<Array<string> | undefined>>>(NOT_FETCHED_ASYNC_STATE);

const INTROSPECTION_GLOBAL_STATE_KEY = 'query-api-introspection';

function getIntrospectionStateKey(schemaNamesFilter?: Array<string>): string {
  return schemaNamesFilter && schemaNamesFilter.length > 0
    ? `${INTROSPECTION_GLOBAL_STATE_KEY}-${sortedBy(schemaNamesFilter, [(value) => value]).join(',')}`
    : INTROSPECTION_GLOBAL_STATE_KEY;
}

export function clearQueryApiIntrospection(schemaNamesFilter?: Array<string>): void {
  const stateKey = getIntrospectionStateKey(schemaNamesFilter);
  queryApiIntrospectionQueryGlobalState.deleteState(stateKey);
}

export function fetchQueryApiIntrospection(schemaNamesFilter?: Array<string>): void {
  const stateKey = getIntrospectionStateKey(schemaNamesFilter);
  const state = queryApiIntrospectionQueryGlobalState.getState(stateKey);

  if (state.status === AsyncStatus.NOT_FETCHED) {
    makeAsyncCall({
      makeCall: fetchQueryApiSDLDAO,
      args: schemaNamesFilter ? [schemaNamesFilter] : [],
      asyncState: state,
      setAsyncState: (newState) => {
        queryApiIntrospectionQueryGlobalState.updateState(stateKey, newState);
      },
    });
  }
}

const TYPE_EXTENSION_GLOBAL_STATE_KEY = 'query-api-type-extension';

/**
 * Map of type name -> type extension string, e.g. {[type]: "extend type <type> {...backlink fields...})"},
 * or `null` if the type does not have a type extension.
 */
const queryApiTypeExtensionGlobalState = makeUseGlobalClientState<
  Record<string, AsyncState<{typeExtension: string | null}, Array<string>> | undefined>
>({});

export function fetchQueryApiTypeExtension(typeName: string): void {
  const state = queryApiTypeExtensionGlobalState.getState(TYPE_EXTENSION_GLOBAL_STATE_KEY);
  const typeNameState = state[typeName];

  if (typeNameState == null || typeNameState.status === AsyncStatus.NOT_FETCHED) {
    makeAsyncCall({
      makeCall: fetchQueryApiBacklinksDAO,
      args: [typeName],
      asyncState: typeNameState ?? NOT_FETCHED_ASYNC_STATE,
      setAsyncState: (newState) => {
        queryApiTypeExtensionGlobalState.updateState(TYPE_EXTENSION_GLOBAL_STATE_KEY, {
          ...state,
          [typeName]: newState,
        });
      },
    });
  }
}

export function useQueryApiExtensionStatus(typeName: string): AsyncStatus {
  useOnMount(() => {
    fetchQueryApiTypeExtension(typeName);
  });
  return (
    queryApiTypeExtensionGlobalState.useGlobalClientState(TYPE_EXTENSION_GLOBAL_STATE_KEY)[0][typeName]
      ?.status ?? AsyncStatus.NOT_FETCHED
  );
}

function useQueryApiSDL(
  schemaNamesFilter?: Array<string>
): AsyncState<string, Array<Array<string> | undefined>> {
  const stateKey = getIntrospectionStateKey(schemaNamesFilter);
  useEffect(() => {
    fetchQueryApiIntrospection(schemaNamesFilter);
  }, [schemaNamesFilter]);
  return queryApiIntrospectionQueryGlobalState.useGlobalClientState(stateKey)[0];
}

export default function useQueryApiIntrospection(
  schemaNamesFilter?: Array<string>
): UseQueryApiIntrospectionOutput {
  const sdlState = useQueryApiSDL(schemaNamesFilter);
  const typeExtensionState = queryApiTypeExtensionGlobalState.useGlobalClientState(
    TYPE_EXTENSION_GLOBAL_STATE_KEY
  )[0];
  const sdl = sdlState.status === AsyncStatus.SUCCESS ? sdlState.data : null;

  const queryApiSchema = useMemo(() => {
    if (sdl == null) {
      return null;
    }
    const perfId = start('useQueryApiIntrospection', {enabled: true, getMetadata: () => ({})});
    const extensions = Object.values(typeExtensionState)
      .filter(
        (value): value is SuccessAsyncState<{typeExtension: string}> =>
          value?.status === AsyncStatus.SUCCESS && value.data.typeExtension != null
      )
      .map((value) => value.data.typeExtension);

    const sdlWithBacklinks = `${sdl}\n${extensions.join('\n')}`;
    const introspection = measureFn(perfId, () => buildQueryApiIntrospection(sdlWithBacklinks));
    stop(perfId);
    return introspection;
  }, [sdl, typeExtensionState]);

  return useMemo(
    () =>
      sdlState.status === AsyncStatus.ERROR
        ? {status: QueryResultType.ERROR}
        : sdlState.status === AsyncStatus.LOADING || sdlState.status === AsyncStatus.NOT_FETCHED
          ? {status: QueryResultType.PENDING}
          : {
              status: QueryResultType.SUCCESS,
              ...queryApiSchema!,
            },
    [sdlState.status, queryApiSchema]
  );
}
